import React, { useMemo } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { toggleReferenceQuestion } from "../../store/slices/pulseCheckSlice";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHOptionCard from "../../components/LHOptionCard";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";
import type { ThemeColors } from "../../theme/types";

interface Option {
  id: string;
  label: string;
  Pallet_Type?: string;
}

interface PulseCheckQuestionsProps {
  questions: Option[];
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 24,
      borderRadius: 6,
      overflow: 'hidden',
      elevation: 4,
      shadowColor: 'rgba(0, 94, 192, 0.08)',
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    sectionHeader: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    header1text: {
      fontSize: 16,
    },
    optionGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: Platform.OS === "web" ? "flex-start" : "space-between",
      gap: Platform.OS === "web" ? 16 : 6,
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      padding: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderTopWidth: 0,
      borderColor: colors.border.default,
    },
  });

const PulseCheckQuestions: React.FC<PulseCheckQuestionsProps> = ({
  questions,
}) => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const selectedReferenceQuestions = useSelector(
    (state: RootState) => state.pulseCheck.selectedReferenceQuestions
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );

  const handleToggleReferenceQuestion = (option: Option): void => {
    dispatch(
      toggleReferenceQuestion({
        id: option.id,
        label: option.label,
        Pallet_Type: option.Pallet_Type,
      })
    );
  };

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title="Reference Questions"
        containerStyle={styles.sectionHeader}
        textStyle={styles.header1text}
      />
      <View style={styles.optionGrid}>
        {questions.map((option) => (
          <LHOptionCard
            key={option.id}
            title={option.label}
            selected={selectedReferenceQuestions.some(
              (item) => item.id === option.id
            )}
            onPress={() => handleToggleReferenceQuestion(option)}
            disabled={pulseState === "completed"}
          />
        ))}
      </View>
    </View>
  );
};

export default PulseCheckQuestions;
